import { Client, IMessage } from '@stomp/stompjs';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// ── WebSocket URL ──────────────────────────────────────
const NOTIFICATION_TOPIC = '/topic/notification';

function getWebSocketUrl(): string {
    const base = process.env.REACT_APP_BASE_URL || '';
    if (base) {
        const url = new URL(base);
        const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${url.host}/api/gs-ws`;
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/api/gs-ws`;
}

// ── Types ──────────────────────────────────────────────
export type NotificationCategory = 'USERS' | 'SYSTEM';

export interface AppNotification {
    id: number;
    category: NotificationCategory;
    body: string;
    at: string;
    read: boolean;
}

interface NotificationContextValue {
    notifications: AppNotification[];
    unreadCount: number;
    connected: boolean;
    error: string | null;
    markAllRead: () => void;
    /** Добавить уведомление вручную (для системных событий) */
    addNotification: (n: Omit<AppNotification, 'id' | 'read'>) => void;
}

// ── Context ────────────────────────────────────────────
const NotificationContext = createContext<NotificationContextValue | null>(null);

// ── Provider ───────────────────────────────────────────
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const idRef = useRef(0);

    const addNotification = useCallback((n: Omit<AppNotification, 'id' | 'read'>) => {
        setNotifications(prev => [...prev, { ...n, id: ++idRef.current, read: false }]);
    }, []);

    const markAllRead = useCallback(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }, []);

    useEffect(() => {
        const client = new Client({
            brokerURL: getWebSocketUrl(),
            reconnectDelay: 3000,
            onConnect: () => {
                setConnected(true);
                setError(null);
                client.subscribe(NOTIFICATION_TOPIC, (message: IMessage) => {
                    const body = (JSON.parse(message.body)?.payload as string) ?? message.body;
                    setNotifications(prev => [
                        ...prev,
                        {
                            id: ++idRef.current,
                            category: 'USERS',
                            body,
                            at: new Date().toLocaleTimeString('ru-RU'),
                            read: false,
                        },
                    ]);
                });
            },
            onStompError: (frame) => {
                setError(frame.headers?.message || 'WebSocket error');
                setConnected(false);
            },
            onWebSocketClose: () => {
                setConnected(false);
            },
        });

        client.activate();
        return () => { client.deactivate(); };
    }, []);

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <NotificationContext.Provider value={{ notifications, unreadCount, connected, error, markAllRead, addNotification }}>
            {children}
        </NotificationContext.Provider>
    );
};

// ── Hook ───────────────────────────────────────────────
export function useNotifications(): NotificationContextValue {
    const ctx = useContext(NotificationContext);
    if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
    return ctx;
}
